import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import AIModel from 'types/ai_model';
import APIError from 'types/api_error';

import { useAPI } from 'hooks/api/useAPI';
import { useAppQueryClient } from 'hooks/useAppQueryClient';

import { marketplaceModelsQueryKey } from './useModels';
import { useAuthenticatedQuery } from './useAuthenticatedQuery';
import { UseCreateModelVariables, UseUpdateModelVariables } from './useModel';
import { useDangerousMutation } from './useDangerousMutation';

export function useAdminMarketplaceModel(
  modelID?: AIModel['id'],
  options?: Omit<UseQueryOptions<AIModel, APIError>, 'select'>
) {
  const api = useAPI();
  const queryClient = useQueryClient();
  const placeholderData =
    queryClient
      .getQueryData<AIModel[]>(['admin-marketplace-models'])
      ?.find(({ id }) => id === modelID) ??
    queryClient
      .getQueryData<AIModel[]>(marketplaceModelsQueryKey(api.applicationID))
      ?.find(({ id }) => id === modelID);

  return useAuthenticatedQuery<AIModel>(
    ['admin-marketplace-model', modelID],
    () => {
      if (!modelID) {
        return Promise.reject('No model id provided.');
      }

      return api.models.marketplace.admin.read(modelID);
    },
    {
      ...options,
      select: (marketplaceModel) => {
        marketplaceModel.source = 'marketplace';
        return marketplaceModel;
      },
      enabled: !!modelID,
      placeholderData,
    }
  );
}

export function useUpdateMarketplaceModel({
  onMutate,
  onSettled,
  ...options
}: UseMutationOptions<AIModel, APIError, UseUpdateModelVariables> = {}) {
  const api = useAPI();
  const queryClient = useAppQueryClient();

  return useMutation(
    ({ model, files = {} }) =>
      api.models.marketplace.admin.update(model, files),
    {
      ...options,
      onMutate: ({ model }) => {
        const previousModels =
          queryClient.getQueryData<AIModel[]>(['admin-marketplace-models']) ||
          [];

        if (previousModels) {
          queryClient.setQueryData(
            ['admin-marketplace-models'],
            previousModels.map((previousModel) => {
              if (previousModel.id === model.id) {
                return model;
              }

              return previousModel;
            })
          );
        }

        return { previousModels };
      },
      onSettled: (model, ...args) => {
        if (onSettled) {
          onSettled(model, ...args);
        }

        queryClient.invalidateQueries(['admin-marketplace-models']);
        queryClient.invalidateQueries(['marketplace-models']);

        if (model) {
          queryClient.invalidateQueries(['admin-marketplace-model', model.id]);
        }
      },
    }
  );
}

export function useCreateMarketplaceModel({
  onMutate,
  onSuccess,
  onSettled,
  ...options
}: UseMutationOptions<AIModel, APIError, UseCreateModelVariables> = {}) {
  const api = useAPI();
  const queryClient = useAppQueryClient();

  return useMutation(
    ({ model, files = {} }) =>
      api.models.marketplace.admin.create(model, files),
    {
      ...options,
      onSuccess: async (model, variables, context) => {
        await queryClient.invalidateQueries(['admin-marketplace-models']);
        await queryClient.invalidateQueries(['marketplace-models']);

        if (onSuccess) {
          onSuccess(model, variables, context);
        }
      },
      onSettled: (...args) => {
        if (onSettled) {
          onSettled(...args);
        }

        queryClient.invalidateQueries(['admin-marketplace-models']);
        queryClient.invalidateQueries(['marketplace-models']);
      },
    }
  );
}

type UseDeleteModelContext = {
  previousModels: AIModel[] | undefined;
} | void;

export function useDeleteMarketplaceModel({
  onMutate,
  onError,
  onSettled,
}: UseMutationOptions<void, APIError, AIModel, UseDeleteModelContext> = {}) {
  const api = useAPI();
  const queryClient = useAppQueryClient();

  return useDangerousMutation<void, UseDeleteModelContext, AIModel>(
    'Are you sure you want to delete this AI model?',
    (model) => api.models.marketplace.admin.delete(model.id),
    {
      onMutate: async (model) => {
        await queryClient.cancelQueries(['admin-marketplace-models']);
        const models =
          queryClient.getQueryData<AIModel[]>(['admin-marketplace-models']) ||
          [];
        const updatedModels = models.filter(({ id }) => id !== model.id);

        queryClient.setQueryData(['admin-marketplace-models'], updatedModels);

        onMutate?.(model);

        return { previousModels: models };
      },
      onError: (err, deletedModel, context) => {
        if (context?.previousModels) {
          queryClient.setQueryData(
            ['admin-marketplace-models'],
            context.previousModels
          );
        }

        onError?.(err, deletedModel, context);
      },
      onSettled: (...args) => {
        queryClient.invalidateQueries(['admin-marketplace-models']);
        queryClient.invalidateQueries(['marketplace-models']);
        onSettled?.(...args);
      },
    }
  );
}
